import { useCallback, useEffect, useRef, useState } from "react";
import Map3D, { ProjectionFnParamType } from "./map3d";
import { GeoJsonType } from "./map3d/typed";
import {
  findRegionFeature,
  loadLocalGeoJson,
  loadTownGeoJson,
  loadTownshipGeoJson,
  sliceTownFeature,
} from "./geo/townshipLoader";

// 地图放大倍率
const MapScale: Record<string, number> = {
  province: 100,
  city: 200,
  district: 300,
  street: 450,
  town: 700,
};

const ROOT_ADCODE = 100000;
const ROOT_PROJECTION: ProjectionFnParamType = {
  center: [104.0, 37.5],
  scale: 40,
};

/** 点击某级区域时，面包屑应保留到父级为止（防止失败态继续追加兄弟节点） */
const PATH_PARENT_DEPTH: Record<string, number> = {
  province: 0,
  city: 1,
  district: 2,
  street: 3,
};

function nextPathByLevel(
  pathNames: string[],
  level: string,
  name: string
): string[] {
  const depth = PATH_PARENT_DEPTH[level];
  if (depth == null) {
    return [...pathNames, name];
  }
  return [...pathNames.slice(0, depth), name];
}

type MapViewState = {
  /** 行政 adcode；乡镇层为 null */
  adcode: number | null;
  /** 乡镇层使用中文路径面包屑 */
  pathNames: string[];
  /** admin=public/geo；township=区下乡镇列表；town=单个乡镇 */
  source: "admin" | "township" | "town";
  projectionFnParam: ProjectionFnParamType;
};

type MapHistoryItem = MapViewState;

function getAdcodeFromUrl(): number | null {
  const raw = new URLSearchParams(window.location.search).get("adcode");
  if (!raw) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

async function resolveInitialView(): Promise<MapViewState> {
  const urlAdcode = getAdcodeFromUrl();

  if (urlAdcode == null || urlAdcode === ROOT_ADCODE) {
    return {
      adcode: ROOT_ADCODE,
      pathNames: [],
      source: "admin",
      projectionFnParam: ROOT_PROJECTION,
    };
  }

  const feature = await findRegionFeature(urlAdcode);
  if (!feature?.properties) {
    throw new Error(`未找到 adcode=${urlAdcode} 对应区域`);
  }

  const { name, level, centroid, center } = feature.properties;
  return {
    adcode: urlAdcode,
    pathNames: name ? [name] : [],
    source: "admin",
    projectionFnParam: {
      center: centroid || center || ROOT_PROJECTION.center,
      scale: MapScale[level] || MapScale.province,
    },
  };
}

async function loadGeoForView(view: MapViewState): Promise<GeoJsonType> {
  if (view.source === "town") {
    return loadTownGeoJson(view.pathNames);
  }
  if (view.source === "township") {
    return loadTownshipGeoJson(view.pathNames);
  }
  if (view.adcode != null) {
    return loadLocalGeoJson(view.adcode);
  }
  throw new Error("无效的地图视图状态");
}

function assertValidGeoJson(data: GeoJsonType): GeoJsonType {
  if (
    !data ||
    data.type !== "FeatureCollection" ||
    !Array.isArray(data.features) ||
    data.features.length === 0
  ) {
    throw new Error("地图数据无效或为空，请检查 geo JSON 是否部署正确");
  }
  return data;
}

function App() {
  const [geoJson, setGeoJson] = useState<GeoJsonType>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<MapViewState | null>(null);
  const [history, setHistory] = useState<MapHistoryItem[]>([]);
  /** 防止重复点击并发下钻 */
  const navigatingRef = useRef(false);

  /** 先拉数据，成功后再提交视图（地图只重建一次） */
  const commitNavigation = useCallback(
    async (
      next: MapViewState,
      options?: { pushHistory?: boolean; data?: GeoJsonType }
    ) => {
      if (navigatingRef.current) {
        return;
      }
      navigatingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const data = assertValidGeoJson(
          options?.data ?? (await loadGeoForView(next))
        );
        if (options?.pushHistory && view) {
          setHistory((prev) => [...prev, view]);
        }
        // 同步提交：投影参数与 geoJson 一起变，避免旧图配新投影抢先播放下钻动画
        setView(next);
        setGeoJson(data);
      } catch (err: any) {
        const message = err?.message || String(err);
        setError(message);
        console.error(err);
      } finally {
        navigatingRef.current = false;
        setLoading(false);
      }
    },
    [view]
  );

  // 初始：解析 URL → 加载数据 → 再展示
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      setLoading(true);
      try {
        const initial = await resolveInitialView();
        const data = assertValidGeoJson(await loadGeoForView(initial));
        if (!cancelled) {
          setView(initial);
          setGeoJson(data);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || String(err));
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  // 单击下钻：先加载目标数据，完成后再切换
  const dblClickFn = useCallback(
    (customProperties: any) => {
      if (!view || navigatingRef.current) {
        return;
      }

      const level = customProperties?.level as string | undefined;
      const name = customProperties?.name as string | undefined;
      if (!level || !name) {
        return;
      }

      // 单个乡镇为最末级
      if (level === "town") {
        return;
      }

      const nextPathNames = nextPathByLevel(view.pathNames, level, name);
      const nextProjection: ProjectionFnParamType = {
        center:
          customProperties.centroid ||
          customProperties.center ||
          view.projectionFnParam.center,
        scale: MapScale[level] ?? view.projectionFnParam.scale,
      };

      // 乡镇列表 → 单个乡镇
      if (level === "street") {
        const nextView: MapViewState = {
          adcode: null,
          pathNames: nextPathNames,
          source: "town",
          projectionFnParam: {
            ...nextProjection,
            scale: MapScale.town,
          },
        };
        const sliced = geoJson ? sliceTownFeature(geoJson, name) : null;
        void commitNavigation(nextView, {
          pushHistory: true,
          data: sliced ?? undefined,
        });
        return;
      }

      // 区县 → 本地乡镇列表
      if (level === "district") {
        void commitNavigation(
          {
            adcode: null,
            pathNames: nextPathNames,
            source: "township",
            projectionFnParam: {
              ...nextProjection,
              scale: MapScale.street,
            },
          },
          { pushHistory: true }
        );
        return;
      }

      // 东莞/中山等无下级区县的市 → 直接本地乡镇列表
      if (level === "city" && Number(customProperties.childrenNum) === 0) {
        void commitNavigation(
          {
            adcode: null,
            pathNames: nextPathNames,
            source: "township",
            projectionFnParam: {
              ...nextProjection,
              scale: MapScale.street,
            },
          },
          { pushHistory: true }
        );
        return;
      }

      // 其余走本地 public/geo 省 / 市
      if (customProperties.adcode == null) {
        return;
      }

      void commitNavigation(
        {
          adcode: customProperties.adcode,
          pathNames: nextPathNames,
          source: "admin",
          projectionFnParam: nextProjection,
        },
        { pushHistory: true }
      );
    },
    [view, geoJson, commitNavigation]
  );

  const handleBack = useCallback(() => {
    if (history.length === 0 || navigatingRef.current) {
      return;
    }

    const last = history[history.length - 1];
    void (async () => {
      navigatingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const data = assertValidGeoJson(await loadGeoForView(last));
        setHistory((prev) => prev.slice(0, -1));
        setView(last);
        setGeoJson(data);
      } catch (err: any) {
        const message = err?.message || String(err);
        setError(message);
        console.error(err);
      } finally {
        navigatingRef.current = false;
        setLoading(false);
      }
    })();
  }, [history]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {history.length > 0 && (
        <button
          type="button"
          onClick={handleBack}
          disabled={loading}
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1000,
            padding: "8px 16px",
            border: "1px solid #3B93E6",
            background: "rgba(1, 2, 9, 0.85)",
            color: "#3B93E6",
            fontSize: 14,
            cursor: loading ? "wait" : "pointer",
            letterSpacing: "0.05em",
            opacity: loading ? 0.6 : 1,
          }}
        >
          ← 返回
        </button>
      )}

      {view && view.pathNames.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: history.length > 0 ? 110 : 16,
            zIndex: 1000,
            padding: "8px 12px",
            color: "#7eb6e8",
            fontSize: 13,
            pointerEvents: "none",
          }}
        >
          {view.pathNames.join(" / ")}
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 900,
            background: "rgba(1, 2, 9, 0.35)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            padding: 16,
            pointerEvents: "all",
            cursor: "wait",
          }}
        >
          <span style={{ color: "#3B93E6", fontSize: 13 }}>加载中…</span>
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: 1000,
            padding: 12,
            background: "rgba(80,0,0,0.85)",
            color: "#ffb4b4",
            fontSize: 12,
            whiteSpace: "pre-wrap",
          }}
        >
          {error}
        </div>
      )}

      {geoJson && view && (
        <Map3D
          geoJson={geoJson}
          dblClickFn={dblClickFn}
          projectionFnParam={view.projectionFnParam}
        />
      )}
    </div>
  );
}

export default App;
